import React, { useState } from 'react';
import { Check, FolderInput, Loader2, X } from 'lucide-react';
import type { AbschnittZiel } from './arbeitsschritteListeLogik';

// Werte der Auswahl: '' ist der Platzhalter, Ziele tragen ein Präfix (auch
// „Ohne Abschnitt“ mit dem leeren Schlüssel).
const ZIEL_PRAEFIX = 'ab:';
const NEUER_ABSCHNITT = 'neu';

interface SchrittVerschiebenAuswahlProps {
  aktuell: string;                // Abschnitt des Schritts ('' = ohne)
  ziele: AbschnittZiel[];
  gesperrt: boolean;              // ein Verschiebe-/Zusammenführ-Vorgang läuft
  laeuft: boolean;                // dieser Schritt wird gerade verschoben
  onVerschieben: (ziel: string) => void;
  onNeuerAbschnitt: () => void;
}

/**
 * „In anderen Abschnitt verschieben“ je Zeile: nur das Ordner-Symbol, die
 * native Auswahl liegt unsichtbar darüber (am Handy öffnet sie die
 * Systemliste). Der Klick erreicht die Zeile nicht — sie würde sonst ausgewählt.
 */
export const SchrittVerschiebenAuswahl: React.FC<SchrittVerschiebenAuswahlProps> = ({
  aktuell, ziele, gesperrt, laeuft, onVerschieben, onNeuerAbschnitt,
}) => (
  <label
    onClick={e => e.stopPropagation()}
    className="relative opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100 p-1 text-slate-400 hover:text-blue-500 rounded focus-within:ring-2 focus-within:ring-blue-500/30"
    title="In anderen Abschnitt verschieben"
  >
    {laeuft ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FolderInput className="w-3.5 h-3.5" />}
    <select
      value=""
      disabled={gesperrt}
      onChange={e => {
        const wert = e.target.value;
        if (wert === NEUER_ABSCHNITT) onNeuerAbschnitt();
        else if (wert.startsWith(ZIEL_PRAEFIX)) onVerschieben(wert.slice(ZIEL_PRAEFIX.length));
      }}
      aria-label="In anderen Abschnitt verschieben"
      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
    >
      <option value="" disabled>In anderen Abschnitt verschieben …</option>
      {ziele.filter(z => z.schluessel !== aktuell).map(z => (
        <option key={ZIEL_PRAEFIX + z.schluessel} value={ZIEL_PRAEFIX + z.schluessel}>{z.name}</option>
      ))}
      <option value={NEUER_ABSCHNITT}>Neuer Abschnitt …</option>
    </select>
  </label>
);

/** Eingabe unter der Zeile: neuen Abschnitt anlegen und den Schritt dorthin verschieben. */
export const SchrittNeuerAbschnittEingabe: React.FC<{
  gesperrt: boolean;
  onAnlegen: (name: string) => void;
  onAbbrechen: () => void;
}> = ({ gesperrt, onAnlegen, onAbbrechen }) => {
  const [entwurf, setEntwurf] = useState('');
  const anlegen = () => {
    if (entwurf.trim() && !gesperrt) onAnlegen(entwurf.trim());
  };
  return (
    <div className="mt-1 p-2 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-1">
      <input
        value={entwurf}
        onChange={e => setEntwurf(e.target.value)}
        placeholder="Neuer Abschnitt (gilt für alle Projekte)"
        aria-label="Name des neuen Abschnitts"
        className="flex-1 min-w-0 text-xs px-2 py-1.5 border border-blue-300 rounded bg-white outline-none focus:ring-2 focus:ring-blue-500/20"
        autoFocus
        onKeyDown={e => {
          if (e.key === 'Enter') { e.preventDefault(); anlegen(); }
          if (e.key === 'Escape') onAbbrechen();
        }}
      />
      <button
        onClick={anlegen}
        disabled={!entwurf.trim() || gesperrt}
        className="p-1.5 text-green-600 bg-green-50 rounded border border-green-200 disabled:opacity-50"
        title="Anlegen und verschieben"
      >
        <Check className="w-3.5 h-3.5" />
      </button>
      <button onClick={onAbbrechen} className="p-1.5 text-red-500 bg-red-50 rounded border border-red-200" title="Abbrechen">
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};
